import React from 'react'
import { useAuth } from '../hooks/useAuth'
import { getModuleDisplayName } from '../utils/authSession'

/**
 * Declarative Permission & Module Subscription Guard
 * Wraps routes and components to enforce RBAC and subscription limits without mounting unauthorized components.
 * Admins automatically bypass all checks.
 */
export default function PermissionGuard({
  privilege,
  anyOf,
  module,
  routeLabel = '',
  children,
}) {
  const { hasPrivilege, hasAnyPrivilege, modules, isAdmin } = useAuth()

  // 1. Admin bypass: Instant unrestricted access
  if (isAdmin) {
    return children
  }

  // 2. Module Subscription Check (e.g. nerve, bruno, education, bridge)
  if (module && !modules.includes(String(module).toLowerCase())) {
    const display = getModuleDisplayName(module)
    return (
      <div className="bridge-canvas-access-block" role="alert" aria-live="polite">
        <div className="bridge-canvas-access-card bridge-canvas-access-card--module">
          <div className="bridge-canvas-access-header">
            <span className="material-symbols-outlined">lock</span>
            <h3>Subscription Required</h3>
          </div>
          <p className="bridge-canvas-access-module-badge">{display} Module</p>
          <p>
            Your organization&apos;s current subscription plan does not include this module or it has expired.
          </p>
          <p className="bridge-canvas-access-cta">
            Please contact your administrator to upgrade your plan.
          </p>
        </div>
      </div>
    )
  }

  // 3. RBAC Granular Privilege Check
  const isAllowed = anyOf
    ? hasAnyPrivilege(anyOf)
    : privilege
    ? hasPrivilege(privilege)
    : true

  if (!isAllowed) {
    return (
      <div className="bridge-canvas-access-block" role="alert" aria-live="polite">
        <div className="bridge-canvas-access-card">
          <div className="bridge-canvas-access-header">
            <span className="material-symbols-outlined">lock</span>
            <h3>Access Restricted</h3>
          </div>
          <p>You do not have permission to view this page.</p>
          {routeLabel ? <p className="bridge-canvas-access-route">{routeLabel}</p> : null}
        </div>
      </div>
    )
  }

  // 4. Authorized: render child screen
  return children
}
